import { forwardRef, type ReactNode } from 'react';

import useResponsiveVariant from 'utils/useResponsiveVariant';
import { supplementsAllVariants, supplementsVariantsVideos } from '../utils';

import { ModuleHeroImage } from './ModuleHeroImage';
import { getVariant } from './ModuleHeroUtils';
import { ModuleHeroVideo } from './ModuleHeroVideo';

type ModuleHeroProps = {
  bgLoaded: () => void;
  showArrow?: boolean;
  variant?: (typeof supplementsAllVariants)[number];
  content: {
    title: string | ReactNode;
    paragraphMobile: ReactNode;
    paragraphDesktop: ReactNode;
  };
};

export const ModuleHero = forwardRef<HTMLDivElement, ModuleHeroProps>(
  ({ bgLoaded, showArrow = false, variant, content }, ref) => {
    const { isMobile, isTablet } = useResponsiveVariant();
    const isVariantVideo = variant && supplementsVariantsVideos.includes(variant);

    /* CHORE: Migrate it to supp lp index in a similar fashion as ModuleClinical */
    /* when url parameters aren't named they receive a number key */
    const { contentVariant, testId } = getVariant(variant);
    const contentVariantMedia =
      'video' in contentVariant
        ? contentVariant.video
        : 'image' in contentVariant
        ? contentVariant.image
        : null;

    if (!contentVariantMedia) return null;

    const contentUrl = isMobile
      ? contentVariantMedia.mobile.url
      : isTablet
      ? contentVariantMedia.tablet.url
      : contentVariantMedia.desktop.url;

    const textColor = isMobile
      ? contentVariantMedia.mobile.color
      : isTablet
      ? contentVariantMedia.tablet.color
      : contentVariantMedia.desktop.color;

    return isVariantVideo ? (
      <ModuleHeroVideo
        ref={ref}
        bgLoaded={bgLoaded}
        content={content}
        showArrow={showArrow}
        testId={testId}
        textColor={textColor}
        url={typeof contentUrl === 'string' ? contentUrl : contentUrl.src}
      />
    ) : (
      <ModuleHeroImage
        ref={ref}
        bgLoaded={bgLoaded}
        content={content}
        showArrow={showArrow}
        testId={testId}
        textColor={textColor}
        url={typeof contentUrl === 'string' ? contentUrl : contentUrl.src}
      />
    );
  },
);

ModuleHero.displayName = 'ModuleHero';
